import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  EXPORT_PURCHASES,
  PURCHASES_EXPORT_STATUS,
  GET_PURCHASES_EXPORT_DOWNLOAD_URL,
} from '../gql/exportPurchases';

const usePurchasesExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const subscriptionRef = useRef(null);

  const client = useApolloClient();

  // Export mutation
  const [exportPurchasesMutation] = useMutation(EXPORT_PURCHASES);

  // Start export function
  const startExport = useCallback(
    async (params = {}, format = 'CSV') => {
      try {
        setIsExporting(true);

        // Get user's timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Build the input object with format and timezone
        const input = {
          format: format.toLowerCase(),
          timezone,
        };

        if (params.accountId) {
          input.account_id = params.accountId;
        }

        if (params.year) {
          input.year = params.year;
        }

        if (params.type) {
          input.type = params.type;
        }

        if (params.search) {
          input.queryString = params.search;
        }

        if (params.startDate) {
          input.start_date = params.startDate;
        }
        if (params.endDate) {
          input.end_date = params.endDate;
        }

        if (params.ticketId) {
          input.ticket_id = Number(params.ticketId);
        }

        // Execute the mutation
        const { data } = await exportPurchasesMutation({
          variables: {
            input,
          },
        });

        if (data?.getAccountPurchasesReport?.jobId) {
          setCurrentJobId(data.getAccountPurchasesReport.jobId);
          return data.getAccountPurchasesReport.jobId;
        } else {
          throw new Error('No job ID returned from export');
        }
      } catch (error) {
        setIsExporting(false);
        throw error;
      }
    },
    [exportPurchasesMutation]
  );

  // Subscribe to export progress
  const subscribeToProgress = useCallback(
    (jobId, callbacks = {}) => {
      const { onComplete, onError, onProgress } = callbacks;

      // Clean up any existing subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }

      // Create new subscription
      subscriptionRef.current = client
        .subscribe({
          query: PURCHASES_EXPORT_STATUS,
          variables: { jobId },
        })
        .subscribe({
          next: ({ data }) => {
            const status = data?.accountPurchasesExportStatus;

            if (!status) return;

            if (onProgress && status.progress !== null) {
              onProgress(status);
            }

            if (status.status === 'COMPLETED') {
              setIsExporting(false);
              if (onComplete) {
                onComplete(status);
              }
              if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
              }
            }

            if (status.status === 'FAILED') {
              setIsExporting(false);
              if (onError) {
                onError(new Error(status.error || 'Export failed'));
              }
              if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
              }
            }
          },
          error: (error) => {
            setIsExporting(false);
            if (onError) {
              onError(error);
            }
            if (subscriptionRef.current) {
              subscriptionRef.current.unsubscribe();
              subscriptionRef.current = null;
            }
          },
        });

      return subscriptionRef.current;
    },
    [client]
  );

  // Get download URL
  const getDownloadUrl = useCallback(
    async (jobId) => {
      try {
        const { data } = await client.query({
          query: GET_PURCHASES_EXPORT_DOWNLOAD_URL,
          variables: { jobId: jobId || currentJobId },
          fetchPolicy: 'network-only',
        });

        return data?.getAccountPurchasesExportDownloadUrl;
      } catch (error) {
        throw error;
      }
    },
    [client, currentJobId]
  );

  // Clean up subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  return {
    startExport,
    subscribeToProgress,
    getDownloadUrl,
    isExporting,
    currentJobId,
  };
};

export default usePurchasesExport;
